import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { showErrorToast, showWarningToast } from '@Helpers';

import { ModalComponent, ModalLoading, ModalSuccess } from '@Components';
import { Combobox, ComboboxContent, ComboboxInput, ComboboxItem, ComboboxList } from '@Components/ui-v2/combobox';
import { Input } from '@Components/ui-v2/input';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { useFetchData } from '@CustomHooks';
import { PlanOption } from '@Models';
import { bopsApi } from '@Network';
import config from '../../config';

interface SupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  fetchSuppliers: () => void;
  setloading: React.Dispatch<React.SetStateAction<boolean>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
}

interface FormFields {
  gln: string;
  displayName: string;
  planId: string;
}

const SupplierAddModal = ({ isOpen, onClose, fetchSuppliers, setloading, setSearch }: SupplierModalProps) => {
  const { t } = useTranslation();

  const [openSuccess, setOpenSuccess] = useState(false);
  const [openLoading, setOpenLoading] = useState(false);
  const [name, setName] = useState('');
  const [errorExist, setErrorExist] = useState('');

  const [siebelLookupError, setSiebelLookupError] = useState('');
  const [isLookingUpGln, setIsLookingUpGln] = useState(false);
  // The GLN this form was last successfully prefilled from — not render
  // state, just a guard `handleGlnBlur` reads/writes.
  const lastLookedUpGln = useRef<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormFields>();

  const planSelected = watch('planId');
  const glnField = register('gln', {
    required: `GLN ${t('Global.Required')}`,
  });

  const { data: planOptions } = useFetchData(
    bopsApi.getSupplierPlanOptions,
    null,
    !(config.isLogycaFlavor || config.isGs1Flavor),
  );
  // Plans requiring a custom dashboard (Avanzado) can't be self-serviced at
  // creation either — provisioning that dashboard is a manual, ops-only
  // step. Mirrors the same restriction on the "Actualizar plan" modal.
  // GS1's own retailers plan is excluded server-side already (never a
  // creation destination), so this filter doesn't apply there.
  const creatablePlanOptions = config.isGs1Flavor
    ? planOptions ?? []
    : (planOptions ?? []).filter((plan) => !plan.allows_custom);

  useEffect(() => {
    reset();
    setErrorExist('');
    setSiebelLookupError('');
    lastLookedUpGln.current = null;
  }, [isOpen]);

  const handleGlnBlur = async (gln: string) => {
    if (!config.isGs1Flavor || !gln) return;
    // Blurring the same GLN again (e.g. clicking back in to double-check
    // it) must not re-run the prefill — it would silently clobber a Name/
    // Plan the admin already hand-corrected. Only a genuine GLN change
    // re-triggers it.
    if (gln === lastLookedUpGln.current) return;

    setSiebelLookupError('');
    setIsLookingUpGln(true);
    try {
      const { name: siebelName, plan_id: siebelPlanId } = await bopsApi.getSiebelLookup(gln);
      setValue('displayName', siebelName);
      if (siebelPlanId) setValue('planId', siebelPlanId);
      lastLookedUpGln.current = gln;
      // plan_id can come back null (the resolved plan didn't exist) — Plan
      // isn't actually touched in that case, so the warning must not claim
      // it was.
      showWarningToast(
        t(siebelPlanId ? 'Supplier.SiebelPrefillOverwrittenNameAndPlan' : 'Supplier.SiebelPrefillOverwrittenNameOnly'),
      );
    } catch {
      // Admin can still fill Name/Plan manually — this is a prefill
      // convenience, not a blocker.
      setSiebelLookupError(t('Supplier.SiebelLookupError'));
    } finally {
      setIsLookingUpGln(false);
    }
  };

  const onSubmit = (data: FormFields) => {
    const { gln, displayName, planId } = data;
    setName(displayName);
    setOpenLoading(true);
    setloading(true);
    setErrorExist('');
    bopsApi
      .postSupplier({
        gln,
        display_name: displayName,
        ...((config.isLogycaFlavor || config.isGs1Flavor) && { plan_id: planId }),
      })
      .then(() => {
        setloading(false);
        onClose();
        setOpenLoading(false);
        setOpenSuccess(true);
        fetchSuppliers();
        setSearch('');
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.detail;
        const textError = t(errorMessage === 'Already exists' ? 'Supplier.Exist' : 'Supplier.Error');
        setloading(false);
        setOpenLoading(false);
        showErrorToast(textError);
        if (errorMessage === 'Already exists') setErrorExist(textError);
      });
  };

  return (
    <>
      <ModalComponent isOpen={isOpen} onClose={onClose} title={t('Supplier.TitleModal')} closeClickOutside={false}>
        <div className="w-full">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="pr-[20px]">
              <span className="text-gray-900 text-sm" id="email">
                GLN*
              </span>
              <Input
                type="number"
                id="gln"
                className={`${
                  errors?.gln || errorExist ? 'border-red focus:border-red' : 'focus:border-gray-600'
                } mb-2`}
                {...glnField}
                onBlur={async (e) => {
                  await glnField.onBlur(e);
                  await handleGlnBlur(e.target.value);
                }}
              />
            </div>
            {(errors?.gln || errorExist) && <p className="text-sm text-red">{errors?.gln?.message ?? errorExist}</p>}
            {isLookingUpGln && (
              <p className="flex items-center gap-1 text-sm text-gray-500">
                <Loader2 className="size-3.5 animate-spin" />
                {t('Supplier.SiebelLookupPending')}
              </p>
            )}
            {siebelLookupError && <p className="text-sm text-red">{siebelLookupError}</p>}
            <div className="pr-[20px]">
              <span className="text-gray-900 text-sm" id="email">
                {`${t('Supplier.Name')}*`}
              </span>
              <Input
                type="text"
                id="displayName"
                className={`${errors?.displayName ? 'border-red focus:border-red' : 'focus:border-gray-600'} mb-2`}
                {...register('displayName', {
                  required: `${t('Supplier.Name')} ${t('Global.Required')}`,
                })}
              />
            </div>
            {errors?.displayName && <p className="text-sm text-red">{errors?.displayName?.message}</p>}

            {config.isLogycaFlavor && (
              <div className="pr-[20px] mt-4 text-sm">
                <span className="text-gray-900 text-sm">{`${t('Supplier.SelectPlan')}*`}</span>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name="planId"
                  render={({ field: { onChange } }) => (
                    <Combobox
                      items={creatablePlanOptions}
                      itemToStringLabel={(item: PlanOption) => item.display_name}
                      value={creatablePlanOptions.find((p) => p.id === planSelected) ?? null}
                      onValueChange={(item: PlanOption | null) => {
                        onChange(item?.id || '');
                      }}>
                      <ComboboxInput placeholder={t('Supplier.SelectPlan')} />
                      <ComboboxContent>
                        <ComboboxList>
                          {(item: PlanOption) => (
                            <ComboboxItem key={item.id} value={item}>
                              {item.display_name}
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                  )}
                />
                {errors.planId && <p className="text-sm text-red">{`${t('Supplier.PlanRequired')}`}</p>}
              </div>
            )}

            {config.isGs1Flavor && (
              <div className="pr-[20px] mt-4 text-sm">
                <span className="text-gray-900 text-sm">{`${t('Supplier.SelectPlan')}*`}</span>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name="planId"
                  render={({ field: { onChange } }) => (
                    <Combobox
                      items={creatablePlanOptions}
                      itemToStringLabel={(item: PlanOption) => item.display_name}
                      value={creatablePlanOptions.find((p) => p.id === planSelected) ?? null}
                      onValueChange={(item: PlanOption | null) => {
                        onChange(item?.id || '');
                      }}>
                      <ComboboxInput placeholder={t('Supplier.SelectPlan')} />
                      <ComboboxContent>
                        <ComboboxList>
                          {(item: PlanOption) => (
                            <ComboboxItem key={item.id} value={item}>
                              {item.display_name}
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                  )}
                />
                {errors.planId && <p className="text-sm text-red">{`${t('Supplier.PlanRequired')}`}</p>}
              </div>
            )}

            <div className="flex gap-2 justify-center mt-4">
              <TrackedButton
                variant="outline"
                buttonName="ADD_SUPPLIER_CANCELED"
                className="w-full bg-white"
                onClick={() => {
                  onClose();
                }}
                isSubmit={false}>
                {t('Global.Canceled')}
              </TrackedButton>
              <TrackedButton buttonName="SUPPLIER_ADDED" isSubmit className="w-full">
                <div className="text-white"> {t('Supplier.Add')} </div>
              </TrackedButton>
            </div>
          </form>
        </div>
      </ModalComponent>

      <ModalSuccess isOpen={openSuccess} onClose={() => setOpenSuccess(false)}>
        <div className="text-center -mt-4">
          <span className="font-semibold text-[18px] text-center"> {name}</span>
          <span className="text-[18px]"> {t('Supplier.AddMessage')} </span>
        </div>
      </ModalSuccess>

      <ModalLoading isOpen={openLoading} onClose={() => setOpenLoading(false)}>
        <div className="text-center -mt-[9px]">
          <span className="font-semibold text-center"> {name}</span> {t('Supplier.LoadingCreate')}
        </div>
      </ModalLoading>
    </>
  );
};

export default SupplierAddModal;
